/**
 *  @class
 *  @function ShopBestsellers
 */
if (!customElements.get('shop-bestsellers')) {
  class ShopBestsellers extends HTMLElement {
    constructor() {
      super();
      this.grid = null;
      this.products = [];
      this.currentActiveProduct = null;
      this.resizeTimer = null;
    }

    connectedCallback() {
      // Get grid and products
      this.grid = this.querySelector('.shop-bestsellers__grid');
      if (!this.grid) return;

      this.products = Array.from(this.grid.querySelectorAll('.shop-bestsellers__product'));
      if (!this.products.length) return;

      // Bind methods
      this.handleScroll = this.handleScroll.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.updateActiveProduct = this.updateActiveProduct.bind(this);

      // Add event listeners
      this.grid.addEventListener('scroll', this.handleScroll);
      window.addEventListener('resize', this.handleResize);

      // Add hover listeners for desktop
      this.products.forEach((product) => {
        product.addEventListener('mouseenter', (e) => this.handleProductHover(e, true));
        product.addEventListener('mouseleave', (e) => this.handleProductHover(e, false));
      });

      // Initialize
      this.init();
    }

    disconnectedCallback() {
      // Clean up event listeners
      if (this.grid) {
        this.grid.removeEventListener('scroll', this.handleScroll);
      }
      window.removeEventListener('resize', this.handleResize);

      // Remove hover listeners
      this.products.forEach((product) => {
        product.removeEventListener('mouseenter', (e) => this.handleProductHover(e, true));
        product.removeEventListener('mouseleave', (e) => this.handleProductHover(e, false));
      });

      if (this.resizeTimer) {
        clearTimeout(this.resizeTimer);
      }
    }

    isMobile() {
      return window.innerWidth <= 767;
    }

    init() {
      if (this.isMobile()) {
        this.updateActiveProduct();
      } else {
        this.pauseAllVideos();
      }
    }

    handleScroll() {
      if (this.isMobile()) {
        this.updateActiveProduct();
      }
    }

    handleResize() {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => {
        this.currentActiveProduct = null;
        this.init();
      }, 100);
    }

    handleProductHover(e, isEntering) {
      // Only handle hover in desktop
      if (this.isMobile()) return;

      const product = e.currentTarget;
      const hasVideo = product.getAttribute('data-has-video') === 'true';
      const videoContainer = product.querySelector('.shop-bestsellers__product-video');
      const videoEl = videoContainer ? videoContainer.querySelector('video') : null;

      if (!hasVideo || !videoEl) return;

      if (isEntering) {
        this.playVideo(videoEl);
      } else {
        this.pauseVideo(videoEl);
      }
    }

    updateActiveProduct() {
      if (!this.isMobile()) return;

      const gridRect = this.grid.getBoundingClientRect();
      const gridCenter = gridRect.left + gridRect.width / 2;

      let closestProduct = null;
      let closestDistance = Infinity;

      // Find the product closest to the center
      this.products.forEach((product) => {
        const productRect = product.getBoundingClientRect();
        const productCenter = productRect.left + productRect.width / 2;
        const distance = Math.abs(gridCenter - productCenter);

        if (distance < closestDistance) {
          closestDistance = distance;
          closestProduct = product;
        }
      });

      // Only update if the active product changed
      if (closestProduct === this.currentActiveProduct) {
        return;
      }

      this.currentActiveProduct = closestProduct;

      // Update all products
      this.products.forEach((product) => {
        const hasVideo = product.getAttribute('data-has-video') === 'true';
        const videoContainer = product.querySelector('.shop-bestsellers__product-video');
        const videoEl = videoContainer ? videoContainer.querySelector('video') : null;

        if (product === closestProduct) {
          // Active product
          product.classList.add('is-active');
          if (hasVideo && videoEl) {
            this.playVideo(videoEl);
          }
        } else {
          // Inactive products
          product.classList.remove('is-active');
          if (hasVideo && videoEl) {
            this.pauseVideo(videoEl);
          }
        }
      });
    }

    playVideo(videoEl) {
      if (!videoEl) return;

      // Ensure video is muted for autoplay
      videoEl.muted = true;
      videoEl.setAttribute('muted', 'muted');

      // Play video
      const playPromise = videoEl.play();
      if (playPromise !== undefined) {
        playPromise.catch((e) => {
          console.log('Video autoplay prevented:', e);
        });
      }
    }

    pauseVideo(videoEl) {
      if (!videoEl) return;
      videoEl.pause();
      videoEl.currentTime = 0;
    }

    pauseAllVideos() {
      this.products.forEach((product) => {
        product.classList.remove('is-active');
        const videoContainer = product.querySelector('.shop-bestsellers__product-video');
        const videoEl = videoContainer ? videoContainer.querySelector('video') : null;
        if (videoEl) {
          this.pauseVideo(videoEl);
        }
      });
    }
  }

  customElements.define('shop-bestsellers', ShopBestsellers);
}
